import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Briefcase, ExternalLink, MapPin, Building2, Clock, ShieldCheck, Share2, Check } from "lucide-react";
import { useState } from "react";
import { listPublicVacancies } from "@/lib/api/vacancies.functions";
import { VacancyAlertsSubscribe } from "@/components/site/VacancyAlertsSubscribe";

export const Route = createFileRoute("/careers")({
  head: () => ({
    meta: [
      { title: "Careers & Vacancies | Nkadimeng Danny Legal" },
      {
        name: "description",
        content:
          "Curated South African job vacancies sourced from legitimate platforms — DPSA, government departments, and reputable job boards. Updated regularly.",
      },
      { property: "og:title", content: "Careers & Vacancies — Nkadimeng Danny" },
      {
        property: "og:description",
        content:
          "Browse verified vacancies and trusted SA job boards. We share opportunities so our clients can grow with us.",
      },
      { property: "og:url", content: "https://nkadimengdannylcs.com/careers" },
    ],
    links: [{ rel: "canonical", href: "https://nkadimengdannylcs.com/careers" }],
  }),
  component: CareersPage,
});

const LEGIT_SOURCES = [
  {
    name: "Eskom Careers",
    url: "https://eskomcareers.ci.hr/applicant/index.php?controller=Page&name=jobsearch",
    note: "Official Eskom recruitment portal — engineering, artisan, technical and corporate roles.",
  },
  {
    name: "DPSA Vacancy Circulars",
    url: "https://www.dpsa.gov.za/dpsa2g/vacancies.asp",
    note: "Official Department of Public Service & Administration weekly circulars.",
  },
  {
    name: "GOV.ZA Vacancies",
    url: "https://www.gov.za/about-government/vacancies",
    note: "Vacancies across all national and provincial government departments.",
  },
  {
    name: "SA Government Jobs Portal",
    url: "https://www.sajobs.gov.za/",
    note: "Centralised government careers portal.",
  },
  {
    name: "Careers24",
    url: "https://www.careers24.com/",
    note: "Large private-sector job board (verified employers).",
  },
  {
    name: "PNet",
    url: "https://www.pnet.co.za/",
    note: "Professional & technical roles across SA.",
  },
  {
    name: "JobMail",
    url: "https://www.jobmail.co.za/",
    note: "Entry-level, artisan & general jobs.",
  },
  {
    name: "LinkedIn Jobs (South Africa)",
    url: "https://www.linkedin.com/jobs/search/?location=South%20Africa",
    note: "Professional network — apply with LinkedIn profile.",
  },
  {
    name: "Indeed South Africa",
    url: "https://za.indeed.com/",
    note: "Aggregated listings; always verify employer before applying.",
  },
  {
    name: "SAPS Recruitment",
    url: "https://www.saps.gov.za/careers/career_home.php",
    note: "Official South African Police Service recruitment.",
  },
  {
    name: "SANDF Careers",
    url: "https://www.dod.mil.za/careers/careers.htm",
    note: "South African National Defence Force.",
  },
] as const;

function fmtDate(d: string | null | undefined) {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString("en-ZA", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return d;
  }
}

function CareersPage() {
  const fetchVacancies = useServerFn(listPublicVacancies);
  const { data, isLoading } = useQuery({
    queryKey: ["public-vacancies"],
    queryFn: () => fetchVacancies(),
  });

  const vacancies = data ?? [];

  return (
    <div className="container-narrow py-12">
      <header className="max-w-3xl">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Careers</p>
        <h1 className="mt-2 font-display text-4xl text-navy">
          Vacancies &amp; Opportunities
        </h1>
        <p className="mt-3 text-foreground/80">
          We share verified job opportunities sourced from legitimate South
          African platforms so our clients and community can grow with us. All
          listings link directly to the official employer or trusted job board —
          we never charge for job referrals.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-foreground/70">
          <ShieldCheck className="h-4 w-4 text-navy" />
          Always verify before paying any fees. Legitimate employers never ask
          applicants for money.
        </div>
      </header>

      <div className="mt-8">
        <VacancyAlertsSubscribe />
      </div>

      <section className="mt-10">
        <div className="mb-4 flex items-end justify-between">
          <h2 className="font-display text-2xl text-navy">Current vacancies</h2>
          <span className="text-xs text-muted-foreground">
            {vacancies.length} active
          </span>
        </div>

        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading vacancies…</p>
        ) : vacancies.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-muted/30 p-8 text-center">
            <Briefcase className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mt-3 text-sm text-foreground/70">
              No curated vacancies at the moment. Please use the trusted sources
              below — we update this board weekly.
            </p>
          </div>
        ) : (
          <ul className="grid gap-4 md:grid-cols-2">
            {vacancies.map((v) => (
              <li
                key={v.id}
                id={`vac-${v.id}`}
                className="scroll-mt-24 rounded-lg border border-border bg-card p-5 shadow-soft transition hover:border-navy"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <Link
                      to="/careers/$id"
                      params={{ id: v.id }}
                      className="font-display text-lg text-navy hover:underline"
                    >
                      {v.title}
                    </Link>
                    <p className="mt-1 flex items-center gap-1.5 text-sm text-foreground/80">
                      <Building2 className="h-3.5 w-3.5" /> {v.employer}
                    </p>
                  </div>
                  {v.sector && (
                    <span className="rounded bg-navy/5 px-2 py-0.5 text-[10px] uppercase tracking-wider text-navy">
                      {v.sector}
                    </span>
                  )}
                </div>

                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-foreground/70">
                  {v.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {v.location}
                    </span>
                  )}
                  {v.employment_type && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" /> {v.employment_type}
                    </span>
                  )}
                  {v.salary_range && <span>{v.salary_range}</span>}
                </div>

                {v.description && (
                  <p className="mt-3 line-clamp-3 text-sm text-foreground/80">
                    {v.description}
                  </p>
                )}

                <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-border/60 pt-3 text-xs">
                  <span className="text-muted-foreground">
                    Source: <span className="font-medium text-foreground/80">{v.source_name}</span>
                    {v.closing_date && (
                      <> • Closes {fmtDate(v.closing_date)}</>
                    )}
                  </span>
                  <div className="flex items-center gap-2">
                    <ShareButton vacancy={v} />
                    <Link
                      to="/careers/$id"
                      params={{ id: v.id }}
                      className="inline-flex items-center gap-1 rounded border border-navy px-3 py-1.5 text-xs font-medium text-navy hover:bg-navy/5"
                    >
                      View & share
                    </Link>
                    <a
                      href={v.application_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 rounded bg-navy px-3 py-1.5 text-xs font-medium text-navy-foreground hover:bg-navy/90"
                    >
                      Apply <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mt-14">
        <h2 className="font-display text-2xl text-navy">
          Trusted job sources in South Africa
        </h2>
        <p className="mt-2 text-sm text-foreground/70">
          Bookmark these — they are the platforms we monitor to bring you the
          listings above.
        </p>
        <ul className="mt-5 grid gap-3 sm:grid-cols-2">
          {LEGIT_SOURCES.map((s) => (
            <li
              key={s.url}
              className="rounded-md border border-border bg-card p-4 hover:border-navy"
            >
              <a
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start justify-between gap-3"
              >
                <div>
                  <p className="font-medium text-navy">{s.name}</p>
                  <p className="mt-1 text-xs text-foreground/70">{s.note}</p>
                </div>
                <ExternalLink className="h-4 w-4 shrink-0 text-muted-foreground" />
              </a>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-12 rounded-lg border border-gold/40 bg-gold/5 p-6">
        <h3 className="font-display text-lg text-navy">
          Need help with your application?
        </h3>
        <p className="mt-2 text-sm text-foreground/80">
          We offer CV review, cover letter drafting and interview preparation as
          part of our paralegal support services. Bookings apply.
        </p>
        <Link
          to="/book"
          className="mt-4 inline-flex items-center justify-center rounded-md bg-navy px-4 py-2 text-sm font-medium text-navy-foreground hover:bg-navy/90"
        >
          Book a consultation
        </Link>
      </section>
    </div>
  );
}

type ShareVacancy = {
  id: string;
  title: string;
  employer: string;
  location: string | null;
  application_url: string;
};

function ShareButton({ vacancy }: { vacancy: ShareVacancy }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/careers/${vacancy.id}`
      : `https://nkadimengdannylcs.com/careers/${vacancy.id}`;
  const shareText = `${vacancy.title} — ${vacancy.employer}${
    vacancy.location ? ` (${vacancy.location})` : ""
  }`;
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedText = encodeURIComponent(shareText);

  async function handleNative() {
    if (typeof navigator !== "undefined" && "share" in navigator) {
      try {
        await (navigator as any).share({
          title: vacancy.title,
          text: shareText,
          url: shareUrl,
        });
        return;
      } catch {
        // fall through to menu
      }
    }
    setOpen((v) => !v);
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  }


  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleNative}
        className="inline-flex items-center gap-1 rounded border border-input px-2.5 py-1.5 text-xs font-medium hover:border-navy"
        aria-label={`Share ${vacancy.title}`}
      >
        <Share2 className="h-3 w-3" /> Share
      </button>
      {open && (
        <div
          className="absolute right-0 z-10 mt-1 w-44 rounded-md border border-border bg-card p-1 text-xs shadow-lg"
          onMouseLeave={() => setOpen(false)}
        >
          <button
            onClick={copyLink}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-muted"
          >
            {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Share2 className="h-3 w-3" />}
            {copied ? "Link copied" : "Copy link"}
          </button>
          <a
            href={`https://wa.me/?text=${encodedText}%20${encodedUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            className="block rounded px-2 py-1.5 hover:bg-muted"
          >
            WhatsApp
          </a>
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`}
            target="_blank"
            rel="noopener noreferrer"
            className="block rounded px-2 py-1.5 hover:bg-muted"
          >
            Facebook
          </a>
          <a
            href={`https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            className="block rounded px-2 py-1.5 hover:bg-muted"
          >
            X (Twitter)
          </a>
          <a
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            className="block rounded px-2 py-1.5 hover:bg-muted"
          >
            LinkedIn
          </a>
          <a
            href={`mailto:?subject=${encodeURIComponent(vacancy.title)}&body=${encodedText}%20${encodedUrl}`}
            className="block rounded px-2 py-1.5 hover:bg-muted"
          >
            Email
          </a>
        </div>
      )}
    </div>
  );
}
